import { assertNever } from '@dhl/utils'
import {
  type AddressFormat,
  type FieldFormat,
  type FieldName,
  type TextFieldFormat,
} from '@dhl/receiver-address-format-api'

export const hasStrictAutocompleteField = (fieldFormat?: FieldFormat): boolean => {
  switch (fieldFormat?.fieldKind) {
    case 'Autocomplete':
      return fieldFormat.isStrict
    default:
      return false
  }
}

export const isRequired = (fieldFormat: FieldFormat): boolean => {
  switch (fieldFormat?.fieldKind) {
    case 'NotApplicable':
      return false
    case 'Text':
    case 'Autocomplete':
      return fieldFormat.isRequired
    default:
      return false
  }
}

export const getPattern = (fieldFormat: FieldFormat): RegExp | undefined => {
  switch (fieldFormat?.fieldKind) {
    case 'Text':
      return fieldFormat.pattern ? new RegExp(fieldFormat.pattern) : undefined
    default:
      return undefined
  }
}

export const getMaxLength = (fieldFormat: FieldFormat): number | undefined => {
  switch (fieldFormat?.fieldKind) {
    case 'Text':
      return fieldFormat.maxLength
    default:
      return undefined
  }
}

export const defaultDutchPostalCodeFieldFormat: TextFieldFormat = {
  fieldKind: 'Text',
  fieldSize: {
    mobile: 12,
    desktop: 6,
  },
  isLookupable: false,
  isRequired: true,
  maxLength: 6,
  textTransforms: ['Uppercase', 'NoWhitespace'],
  pattern: '^\\d{4}[A-Z]{2}$',
}

export const defaultPostalCodeFieldFormat: TextFieldFormat = {
  fieldKind: 'Text',
  fieldSize: {
    mobile: 12,
    desktop: 6,
  },
  isLookupable: false,
  isRequired: true,
  maxLength: 10,
  textTransforms: ['Uppercase', 'NoWhitespace'],
  pattern: '^[a-zA-Z0-9-]{4,10}$',
}

// AddressFormat

export const isPostalCodeLookupEnabled = (addressFormat: AddressFormat): boolean =>
  addressFormat.lookupModes?.includes('ByPostalCodeAndHouseNumber') ||
  addressFormat.lookupModes?.includes('ByPostalCode') ||
  false

export const hasStrictAutocompleteFields = (addressFormat: AddressFormat): boolean =>
  [
    addressFormat.postalCodeFormat,
    addressFormat.cityFormat,
    addressFormat.streetFormat,
    addressFormat.houseNumberFormat,
    addressFormat.houseNumberSuffixFormat,
    addressFormat.suburbFormat,
    addressFormat.additionalAddressLineFormat,
  ].some(hasStrictAutocompleteField)

export const getFieldFormatByName = (addressFormat: AddressFormat, fieldName: FieldName): FieldFormat | undefined => {
  switch (fieldName) {
    case 'PostalCode':
      return addressFormat.postalCodeFormat
    case 'City':
      return addressFormat.cityFormat
    case 'Street':
      return addressFormat.streetFormat
    case 'HouseNumber':
      return addressFormat.houseNumberFormat
    case 'HouseNumberSuffix':
      return addressFormat.houseNumberSuffixFormat
    case 'Suburb':
      return addressFormat.suburbFormat
    case 'AdditionalAddressLine':
      return addressFormat.additionalAddressLineFormat
    default:
      return assertNever(fieldName)
  }
}

export const defaultDutchAddressFormat: AddressFormat = {
  country: 'NL',
  lookupModes: ['ByPostalCodeAndHouseNumber'],
  ordering: ['PostalCode', 'HouseNumber', 'HouseNumberSuffix'],
  postalCodeFormat: {
    ...defaultDutchPostalCodeFieldFormat,
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
  },
  houseNumberFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 6,
      desktop: 3,
    },
    isLookupable: false,
    isRequired: true,
    maxLength: 10,
    textTransforms: ['NoWhitespace', 'OnlyDigits'],
    pattern: '^[0-9]+$',
  },
  houseNumberSuffixFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 6,
      desktop: 3,
    },
    isLookupable: false,
    isRequired: false,
    textTransforms: [],
    maxLength: 10,
    pattern: '^[a-zA-Z0-9]*$',
  },
  streetFormat: {
    fieldKind: 'Autocomplete',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isStrict: true,
    isRequired: true,
  },
  cityFormat: {
    fieldKind: 'Autocomplete',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isStrict: true,
    isRequired: true,
  },
  suburbFormat: {
    fieldKind: 'NotApplicable',
  },
  additionalAddressLineFormat: {
    fieldKind: 'NotApplicable',
  },
  isPreviewVisible: true,
}

export const defaultSpanishAddressFormat: AddressFormat = {
  country: 'ES',
  lookupModes: ['ByPostalCode'],
  ordering: ['PostalCode', 'City', 'Street', 'HouseNumber', 'HouseNumberSuffix'],
  postalCodeFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isLookupable: false,
    isRequired: true,
    maxLength: 7,
    textTransforms: ['Uppercase'],
    pattern: '^\\d{5}$',
  },
  houseNumberFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 6,
      desktop: 3,
    },
    isLookupable: false,
    isRequired: true,
    maxLength: 10,
    textTransforms: [],
    pattern: '^[0-9]+([-][0-9]+)?$',
  },
  houseNumberSuffixFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 6,
      desktop: 3,
    },
    isLookupable: false,
    isRequired: false,
    textTransforms: [],
    maxLength: 15,
  },
  streetFormat: {
    fieldKind: 'Autocomplete',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isStrict: false,
    isRequired: true,
  },
  cityFormat: {
    fieldKind: 'Autocomplete',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isStrict: true,
    isRequired: true,
  },
  suburbFormat: {
    fieldKind: 'NotApplicable',
  },
  additionalAddressLineFormat: {
    fieldKind: 'NotApplicable',
  },
  isPreviewVisible: false,
}

export const defaultGermanAddressFormat: AddressFormat = {
  country: 'DE',
  lookupModes: ['ByPostalCode'],
  ordering: ['PostalCode', 'City', 'Street', 'HouseNumber', 'HouseNumberSuffix'],
  postalCodeFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isLookupable: false,
    isRequired: true,
    maxLength: 7,
    textTransforms: [],
    pattern: '^\\d{5}$',
  },
  houseNumberFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 6,
      desktop: 3,
    },
    isLookupable: false,
    isRequired: true,
    maxLength: 10,
    textTransforms: [],
    pattern: '^[0-9]+([-][0-9]+)?$',
  },
  houseNumberSuffixFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 6,
      desktop: 3,
    },
    isLookupable: false,
    isRequired: false,
    textTransforms: [],
    maxLength: 15,
  },
  streetFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isLookupable: false,
    textTransforms: [],
    isRequired: true,
    maxLength: 35,
  },
  cityFormat: {
    fieldKind: 'Autocomplete',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isRequired: true,
    isStrict: false,
  },
  suburbFormat: {
    fieldKind: 'NotApplicable',
  },
  additionalAddressLineFormat: {
    fieldKind: 'NotApplicable',
  },
  isPreviewVisible: false,
}

export const defaultRwandeseAddressFormat: AddressFormat = {
  country: 'RW',
  lookupModes: ['ByCountry', 'ByCity'],
  ordering: ['Street', 'HouseNumber', 'HouseNumberSuffix', 'City', 'Suburb', 'AdditionalAddressLine'],
  postalCodeFormat: {
    fieldKind: 'NotApplicable',
  },
  houseNumberFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 6,
      desktop: 3,
    },
    isLookupable: false,
    isRequired: true,
    maxLength: 10,
    textTransforms: [],
  },
  houseNumberSuffixFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 6,
      desktop: 3,
    },
    isLookupable: false,
    isRequired: false,
    maxLength: 10,
    textTransforms: [],
  },
  cityFormat: {
    fieldKind: 'Autocomplete',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isStrict: true,
    isRequired: true,
  },
  suburbFormat: {
    fieldKind: 'Autocomplete',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isStrict: true,
    isRequired: true,
  },
  streetFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isLookupable: false,
    isRequired: true,
    maxLength: 35,
    textTransforms: [],
  },
  additionalAddressLineFormat: {
    fieldKind: 'Text',
    fieldSize: {
      mobile: 12,
      desktop: 6,
    },
    isLookupable: false,
    textTransforms: [],
    maxLength: 35,
    isRequired: false,
  },
  isPreviewVisible: false,
}
